import React from 'react';
import {
  VStack,
  HStack,
  Text,
  Box,
  Card,
  CardBody,
  Icon,
  Badge,
  useColorModeValue,
  RadioGroup,
  SimpleGrid,
  Button,
  Flex,
  Alert,
  AlertIcon,
} from '@chakra-ui/react';
import { FiSettings, FiTool, FiCheck } from 'react-icons/fi';
import { RegistrationData } from '../index';

interface VendorTypeSelectionProps {
  data: RegistrationData;
  updateData: (data: RegistrationData) => void;
  onNext: () => void;
  onPrevious: () => void;
}

const VendorTypeSelection: React.FC<VendorTypeSelectionProps> = ({
  data,
  updateData,
  onNext,
}) => {
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const selectedBg = useColorModeValue('blue.50', 'blue.900');
  const selectedBorder = useColorModeValue('blue.400', 'blue.400');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');

  const handleVendorTypeChange = (value: 'manufacturer' | 'installation') => {
    updateData({
      ...data,
      vendorType: value,
    });
  };

  const vendorTypes = [
    {
      id: 'manufacturer',
      title: 'Product Manufacturer',
      icon: FiSettings,
      description: 'Design, produce and supply furniture products',
      capabilities: ['Product Design', 'Manufacturing', 'Quality Control', 'Supply Chain'],
      examples: 'Furniture makers, component suppliers, textile producers',
    },
    {
      id: 'installation',
      title: 'Installation Services',
      icon: FiTool,
      description: 'Provide installation, assembly and project services',
      capabilities: ['Installation', 'Assembly', 'Project Management', 'Support'],
      examples: 'Installation contractors, assembly teams, service providers',
    },
  ];

  return (
    <VStack spacing={8} align="stretch">
      {/* Selection Required Alert */}
      {!data.vendorType && (
        <Alert status="info" borderRadius="md">
          <AlertIcon />
          <Text fontSize="sm">
            <Text as="span" fontWeight="medium">Please select your vendor type</Text> to describe your business and unlock the appropriate registration requirements.
          </Text>
        </Alert>
      )}

      <RadioGroup 
        value={data.vendorType} 
        onChange={(value) => handleVendorTypeChange(value as 'manufacturer' | 'installation')}
      >
        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
          {vendorTypes.map((type) => {
            const isSelected = data.vendorType === type.id;
            
            return (
              <Card
                key={type.id}
                position="relative"
                cursor="pointer"
                onClick={() => handleVendorTypeChange(type.id as 'manufacturer' | 'installation')}
                bg={isSelected ? selectedBg : undefined}
                borderColor={isSelected ? selectedBorder : borderColor}
                borderWidth={isSelected ? 2 : 1}
                _hover={{ 
                  borderColor: selectedBorder,
                  bg: isSelected ? selectedBg : hoverBg,
                  transform: 'translateY(-2px)',
                  shadow: 'lg'
                }}
                transition="all 0.2s"
                h="auto"
                minH="280px"
              >
                <CardBody p={8}>
                  <VStack align="start" spacing={6} h="full">
                    {/* Header */}
                    <Flex w="full" justify="space-between" align="start">
                      <HStack spacing={4}>
                        <Flex
                          w={12}
                          h={12}
                          bg={isSelected ? 'blue.500' : 'gray.100'}
                          borderRadius="xl"
                          align="center"
                          justify="center"
                        >
                          <Icon
                            as={type.icon}
                            color={isSelected ? 'white' : 'gray.500'}
                            boxSize={6}
                          />
                        </Flex>
                        
                        <VStack align="start" spacing={1}>
                          <Text fontSize="xl" fontWeight="bold">
                            {type.title}
                          </Text>
                          <Text color="gray.600" fontSize="md">
                            {type.description}
                          </Text>
                        </VStack>
                      </HStack>
                      
                      {isSelected && (
                        <Flex
                          w={8}
                          h={8}
                          bg="green.500"
                          borderRadius="full"
                          align="center"
                          justify="center"
                        >
                          <Icon as={FiCheck} color="white" boxSize={4} />
                        </Flex>
                      )}
                    </Flex>

                    {/* Capabilities */}
                    <Box>
                      <Text fontSize="sm" fontWeight="semibold" color="gray.700" mb={3}>
                        Key Capabilities:
                      </Text>
                      <SimpleGrid columns={2} spacing={2}>
                        {type.capabilities.map((capability) => (
                          <HStack key={capability} spacing={2}>
                            <Box
                              w={2}
                              h={2}
                              bg="blue.400"
                              borderRadius="full"
                            />
                            <Text fontSize="sm" color="gray.600">
                              {capability}
                            </Text>
                          </HStack>
                        ))}
                      </SimpleGrid>
                    </Box>

                    {/* Examples */}
                    <Box mt="auto">
                      <Text fontSize="xs" color="gray.500" fontStyle="italic">
                        Examples: {type.examples}
                      </Text>
                    </Box>
                  </VStack>
                </CardBody>
              </Card>
            );
          })}
        </SimpleGrid>
      </RadioGroup>

      {/* Progress Indicator */}
      {data.vendorType && (
        <Box>
          <HStack spacing={3} p={4} bg={selectedBg} borderRadius="lg" borderWidth={1} borderColor={selectedBorder}>
            <Icon as={FiCheck} color="blue.500" />
            <Text fontSize="sm" color="blue.700" fontWeight="medium">
              Vendor type selected: {vendorTypes.find(t => t.id === data.vendorType)?.title}
            </Text>
            <Badge colorScheme="blue" ml="auto">
              Ready to continue
            </Badge>
          </HStack>
        </Box>
      )}


    </VStack>
  );
};

export default VendorTypeSelection; 